import React, { useState, useEffect } from 'react';
import { Tag, Space, message, Empty, Button, Modal } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { getUsers, deleteUser } from '../api';
import { useNavigate } from 'react-router-dom';
import ListToolbar from '../components/ListToolbar';
import { exportToCSV } from '../utils/exportUtils';
import PageTitle from '../components/PageTitle';
import ResizableTable from '../components/ResizableTable';

// 角色配置
const ROLE_CONFIG = {
  1: { label: '房东', color: 'blue' },
  2: { label: '租客', color: 'green' },
  3: { label: '管理员', color: 'red' },
  0: { label: '未知角色', color: 'gray' },
};

// 状态配置
const STATUS_CONFIG = {
  1: { label: '正常', color: 'success' },
  0: { label: '禁用', color: 'warning' },
};

// 筛选用的角色选项（包含“全部”）
const ROLE_FILTER_OPTIONS = [
  { value: '', label: '全部角色' },
  { value: '1', label: '房东' },
  { value: '2', label: '租客' },
  { value: '3', label: '管理员' },
];

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    role: '',
  });
  const [displayColumns, setDisplayColumns] = useState([]);
  
  // 原始表格列配置
  const originalColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 100,
      render: (nickname) => nickname || '未知用户',
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || '未绑定',
    },
    {
      title: '角色',
      key: 'role',
      render: (_, record) => {
        const config = ROLE_CONFIG[record.user_type] || ROLE_CONFIG[0];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      width: 80,
    },
    {
      title: '状态',
      key: 'status',
      render: (_, record) => {
        const config = STATUS_CONFIG[record.status] || STATUS_CONFIG[0];
        return <Tag color={config.color}>{config.label}</Tag>;
      },
      width: 80,
    },
    {
      title: 'OpenID',
      dataIndex: 'openid',
      key: 'openid',
      width: 180,
      render: (openid) => openid || '未绑定',
    },
    {
      title: '公众号OpenID',
      dataIndex: 'mp_openid',
      key: 'mp_openid',
      width: 180,
      render: (mpOpenid) => mpOpenid || '未绑定',
    },
    {
      title: 'UnionID',
      dataIndex: 'unionid',
      key: 'unionid',
      width: 180,
      render: (unionid) => unionid || '未绑定',
    },
    {
      title: '操作',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            onClick={() => navigate(`/user/role/${record.id}`)}
          >
            修改
          </Button>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id, record.nickname)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];
  
  // 从localStorage加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const storageKey = 'userList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    // 根据列状态生成实际列配置（只处理隐藏）
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      
      if (state === 'hidden') {
        return null;
      }
      
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  // 加载用户列表（带详细日志，方便排查）
  const loadUsers = async () => {
    try {
      setLoading(true);
      console.log('开始调用获取用户列表接口');
      const response = await getUsers();
      console.log('接口返回原始数据：', response);

      // 解析数据（兼容直接数组或嵌套数据格式）
      const userList = Array.isArray(response) ? response : response?.data || [];
      console.log('解析后用户数据：', userList);

      setUsers(userList);
      setAllUsers(userList); // 保存原始数据，供筛选使用
      message.success(`加载成功 ${userList.length} 个用户`);
    } catch (error) {
      console.error('加载用户列表失败：', error);
      message.error('加载用户列表失败，请稍后重试');
    } finally {
      setLoading(false);
      console.log('加载流程结束，loading 设为 false');
    }
  };

  // 组件挂载时加载数据
  useEffect(() => {
    loadUsers();
  }, []);

  // 筛选+搜索逻辑（实时触发）
  useEffect(() => {
    let filteredUsers = [...allUsers];

    // 1. 角色筛选（非空时生效）
    if (searchParams.role) {
      const targetRole = Number(searchParams.role);
      filteredUsers = filteredUsers.filter(user => user.user_type === targetRole);
    }

    // 2. 关键词搜索（搜索昵称、手机号）
    if (searchParams.keyword.trim()) {
      const keyword = searchParams.keyword.trim().toLowerCase();
      filteredUsers = filteredUsers.filter(user => 
        (user.nickname || '').toLowerCase().includes(keyword) || 
        (user.phone || '').toLowerCase().includes(keyword)
      );
    }

    // 更新筛选后的用户列表
    setUsers(filteredUsers);
  }, [searchParams, allUsers]);

  // 处理关键词搜索输入
  const handleKeywordChange = (value) => {
    setSearchParams({
      ...searchParams,
      keyword: value,
    });
  };

  // 处理角色筛选变更
  const handleRoleChange = (value) => {
    setSearchParams({
      ...searchParams,
      role: value || '',
    });
  };

  // 重置筛选条件
  const handleReset = () => {
    setSearchParams({
      keyword: '',
      role: '',
    });
  };

  // 刷新数据
  const handleRefresh = () => {
    loadUsers();
  };

  // 导出数据
  const handleExport = () => {
    const exportData = users.map(user => ({
      'ID': user.id,
      '昵称': user.nickname || '未知用户',
      '手机号': user.phone || '未绑定',
      '状态': STATUS_CONFIG[user.status]?.label || '未知',
      '角色': ROLE_CONFIG[user.user_type]?.label || '未知角色',
      'OpenID': user.openid || '未绑定',
      '公众号OpenID': user.mp_openid || '未绑定',
      'UnionID': user.unionid || '未绑定',
    }));

    if (exportToCSV(exportData, '用户列表')) {
      message.success(`成功导出 ${exportData.length} 条数据`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  // 临时测试：直接删除（无确认弹窗）
  const handleDelete = async (id, nickname) => {
    console.log('触发删除逻辑：', { id, nickname });
    
    // 直接调用删除接口
    try {
      setLoading(true);
      console.log('开始调用删除接口，用户ID：', id);
      await deleteUser(id);
      console.log('删除接口调用成功，用户ID：', id);
      message.success('删除用户成功');
      loadUsers();
    } catch (error) {
      console.error('删除用户失败详情：', error);
      const errMsg = error.response?.data?.msg || error.message || '删除失败';
      message.error(`删除用户失败：${errMsg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="用户列表" />

      {/* 统一的工具栏 */}
      <ListToolbar
        searchPlaceholder="搜索昵称/手机号"
        searchValue={searchParams.keyword}
        onSearchChange={handleKeywordChange}
        onSearch={() => {}} // 实时搜索，不需要手动触发
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        filters={[
          {
            key: 'role',
            label: '角色筛选',
            value: searchParams.role,
            onChange: handleRoleChange,
            options: ROLE_FILTER_OPTIONS,
          },
        ]}
      />

      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        storageKey="userList"
        pagination={{ 
          pageSize: 10, 
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条数据`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        scroll={{ x: 1800 }}
        locale={{ emptyText: <Empty description="暂无用户数据" /> }}
        size="small"
      />
    </div>
  );
};

export default UserList;